import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { CreateCustomerRequest, CustomerDto, UpdateCustomerRequest } from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import PartyStatement from './PartyStatement';
import Loading from './Loading';
import Hint from './Hint';
import ListFilter, { useListFilter } from './ListFilter';
import { matchesSearch } from './search';
import { formatMoney, sumAmounts } from './money';

const STATUSES = [
  { value: 'inactive', label: 'الموقوفين' },
  { value: 'all', label: 'الكل' },
] as const;

interface Props {
  companyId: string;
  currency: string;
  canCreate: boolean;
  /** المسح للمحاسب والمالك (`journal.manual`) — والخادم يرفض على أى حال. */
  canDelete: boolean;
  /** يُفتح كشفه مباشرة — من أعمار الديون مثلاً. */
  initialCustomerId?: string | null;
  /** فتح كشف أو قفله يغيّر الرابط — فالرجوع يرجّع للقائمة. بلاه يبقى داخل الشاشة. */
  onSelectCustomer?: (id: string | null) => void;
  onSessionLost: () => void;
}

const PERSON_LABELS = { B: 'منشأة', P: 'فرد' } as const;

interface Draft {
  nameAr: string;
  personType: 'B' | 'P';
  taxRegistrationNumber: string;
  phone: string;
  email: string;
  paymentTermsDays: string;
  isActive: boolean;
}

const EMPTY: Draft = {
  nameAr: '',
  personType: 'B',
  taxRegistrationNumber: '',
  phone: '',
  email: '',
  paymentTermsDays: '0',
  isActive: true,
};

function draftOf(customer: CustomerDto): Draft {
  return {
    nameAr: customer.nameAr,
    personType: customer.personType,
    taxRegistrationNumber: customer.taxRegistrationNumber ?? '',
    phone: customer.phone ?? '',
    email: customer.email ?? '',
    paymentTermsDays: String(customer.paymentTermsDays),
    isActive: customer.isActive,
  };
}

function messageOf(caught: unknown): string {
  if (caught instanceof ProblemError) {
    const paths = caught.problem.issues?.map((issue) => issue.path ?? issue.message) ?? [];
    return (
      (caught.problem.detail ?? caught.problem.title) +
      (paths.length ? ': ' + paths.join('، ') : '')
    );
  }
  return caught instanceof Error ? caught.message : String(caught);
}

/**
 * العملاء: قائمة وإضافة وتعديل. المسح لعميل بلا حركات فقط، ومن عليه حركات يُوقف. الكود يولّده الخادم، والرصيد لا يظهر هنا لأنه لا يُخزَّن
 * فى العميل — يُقرأ من كشف حسابه (يأتى مع الفواتير والتحصيل).
 */
export default function Customers({
  companyId,
  currency,
  canCreate,
  canDelete,
  initialCustomerId = null,
  onSelectCustomer,
  onSessionLost,
}: Props) {
  const [statementOf, setStatementOf] = useState<string | null>(initialCustomerId);
  // الرابط هو المرجع: الرجوع أو بحث يغيّر الطرف من بره فيتبعه الكشف
  useEffect(() => setStatementOf(initialCustomerId), [initialCustomerId]);
  const openStatement = (id: string | null) =>
    onSelectCustomer ? onSelectCustomer(id) : setStatementOf(id);
  // الكشف تحت القائمة: يُنزل إليه بعد ما تُرسم الشاشة (وبعد رجوع الصفحة لأعلاها)
  useEffect(() => {
    if (!statementOf) return;
    const timer = window.setTimeout(
      () => document.getElementById('party-statement')?.scrollIntoView({ block: 'start' }),
      200,
    );
    return () => window.clearTimeout(timer);
  }, [statementOf]);
  const [customers, setCustomers] = useState<CustomerDto[] | null>(null);
  const [filter, setFilter] = useListFilter('', 'Customers');
  const [balances, setBalances] = useState<Map<string, string> | null>(null);
  // الافتراضى النشطون: المرحَّل ثم المعكوس يبقى موقوفًا بنفس الاسم، فلا يظهر مكررًا
  const shownCustomers = (customers ?? []).filter(
    (customer) =>
      (filter.status === 'all' ||
        (filter.status === 'inactive' ? !customer.isActive : customer.isActive)) &&
      matchesSearch(filter.query, [
        customer.code,
        customer.nameAr,
        customer.phone,
        customer.email,
        customer.taxRegistrationNumber,
      ]),
  );
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  // المفتاح يتجدد مع كل تعديل: ضغطتان على «حفظ» لنفس البيانات عميل واحد،
  // وبيانات مختلفة بنفس المفتاح كانت ستُرفض 409
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  // العميل الجارى تعديله — null: النموذج لإضافة عميل جديد
  const [editing, setEditing] = useState<CustomerDto | null>(null);

  const load = useCallback(() => {
    api
      .customers(companyId)
      .then(setCustomers)
      .catch((caught: unknown) => {
        if (caught instanceof UnauthorizedError) onSessionLost();
        else setError(messageOf(caught));
      });
    // الرصيد من الأعمار (الدفتر كله) — من لا يقرأ التقارير لا يرى العمود
    api
      .arAging(companyId, new Date().toISOString().slice(0, 10))
      .then((aging) => setBalances(new Map(aging.rows.map((row) => [row.customerId, row.total]))))
      .catch(() => setBalances(null));
  }, [companyId, onSessionLost]);

  useEffect(load, [load]);

  function update<K extends keyof Draft>(key: K, value: Draft[K]): void {
    setDraft((current) => ({ ...current, [key]: value }));
    setIdempotencyKey(crypto.randomUUID());
    setSaved(null);
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const body: CreateCustomerRequest = {
      nameAr: draft.nameAr,
      personType: draft.personType,
      taxRegistrationNumber: draft.taxRegistrationNumber,
      phone: draft.phone,
      email: draft.email,
      paymentTermsDays: Number(draft.paymentTermsDays),
    };
    try {
      if (editing) {
        const changes: UpdateCustomerRequest = { ...body, isActive: draft.isActive };
        const updated = await api.updateCustomer(companyId, editing.id, changes);
        setSaved('اتعدّل: ' + updated.code + ' — ' + updated.nameAr);
        setEditing(null);
        setDraft(EMPTY);
        load();
        return;
      }
      const created = await api.createCustomer(companyId, idempotencyKey, body);
      setSaved(created.code + ' — ' + created.nameAr);
      setDraft(EMPTY);
      setIdempotencyKey(crypto.randomUUID());
      load();
    } catch (caught) {
      if (caught instanceof UnauthorizedError) onSessionLost();
      else setError(messageOf(caught));
    } finally {
      setSaving(false);
    }
  }

  function startEdit(customer: CustomerDto): void {
    setEditing(customer);
    setDraft(draftOf(customer));
    setSaved(null);
    setError(null);
    window.scrollTo(0, 0);
  }

  function cancelEdit(): void {
    setEditing(null);
    setDraft(EMPTY);
    setError(null);
  }

  async function remove(): Promise<void> {
    if (!editing) return;
    if (!window.confirm('مسح العميل «' + editing.nameAr + '» نهائيًا؟')) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteCustomer(companyId, editing.id);
      setSaved('اتمسح: ' + editing.code + ' — ' + editing.nameAr);
      if (statementOf === editing.id) openStatement(null);
      setEditing(null);
      setDraft(EMPTY);
      load();
    } catch (caught) {
      if (caught instanceof UnauthorizedError) onSessionLost();
      else if (
        caught instanceof ProblemError &&
        caught.problem.issues?.some((i) => i.code === 'IN_USE')
      )
        setError('العميل ده عليه حركات فمينفعش يتمسح — شيل علامة «نشط» واحفظ عشان توقفه.');
      else setError(messageOf(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {canCreate && (
        <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
          <h2>{editing ? 'تعديل العميل ' + editing.code : 'عميل جديد'}</h2>
          <div className="form-row">
            <label className="grow">
              الاسم
              <input
                value={draft.nameAr}
                onChange={(event) => update('nameAr', event.target.value)}
                maxLength={200}
                required
              />
            </label>
            <label>
              النوع
              <select
                value={draft.personType}
                onChange={(event) => update('personType', event.target.value as 'B' | 'P')}
              >
                <option value="B">منشأة (فاتورة)</option>
                <option value="P">فرد (إيصال)</option>
              </select>
              <Hint>
                «منشأة» لشركة أو محل له سجل تجارى، و«شخص» لفرد. يلزم للفاتورة الإلكترونية.
              </Hint>
            </label>
            <label>
              التسجيل الضريبى
              <input
                value={draft.taxRegistrationNumber}
                onChange={(event) => update('taxRegistrationNumber', event.target.value)}
                inputMode="numeric"
                maxLength={32}
              />
              <Hint>
                رقم البطاقة الضريبية (٩ أرقام). مطلوب لو ستُصدر له فاتورة إلكترونية كشركة.
              </Hint>
            </label>
          </div>
          <div className="form-row">
            <label>
              الهاتف
              <input
                value={draft.phone}
                onChange={(event) => update('phone', event.target.value)}
                inputMode="tel"
                maxLength={32}
              />
            </label>
            <label className="grow">
              البريد
              <input
                type="email"
                value={draft.email}
                onChange={(event) => update('email', event.target.value)}
                maxLength={254}
              />
            </label>
            <label>
              مدة السداد (يوم)
              <input
                type="number"
                min={0}
                max={365}
                value={draft.paymentTermsDays}
                onChange={(event) => update('paymentTermsDays', event.target.value)}
              />
              <Hint>كم يومًا تمهل العميل للدفع بعد الفاتورة. بعدها تظهر الفاتورة «متأخرة».</Hint>
            </label>
          </div>
          {editing && (
            <label className="option">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(event) => update('isActive', event.target.checked)}
              />
              <span>نشط</span>
              <Hint>
                العميل الموقوف بيختفى من قوايم الفواتير والتحصيل، وكشف حسابه ورصيده فاضلين زى ما هم.
              </Hint>
            </label>
          )}
          {error && <p className="error">{error}</p>}
          {saved && <p className="sub">تم الحفظ: {saved}</p>}
          <div className="form-actions">
            <button
              type="submit"
              className="primary"
              disabled={saving || draft.nameAr.trim().length < 2}
            >
              {saving ? 'جارى الحفظ…' : editing ? 'حفظ التعديل' : 'حفظ العميل'}
            </button>
            {editing && (
              <button type="button" onClick={cancelEdit} disabled={saving}>
                إلغاء
              </button>
            )}
            {editing && canDelete && (
              <button
                type="button"
                className="danger"
                onClick={() => void remove()}
                disabled={saving}
              >
                مسح العميل
              </button>
            )}
          </div>
        </form>
      )}

      <div className="panel">
        <h2>العملاء</h2>
        {!canCreate && error && <p className="error">{error}</p>}
        {customers === null && !error && <Loading />}
        {customers?.length === 0 && <p className="sub">لا عملاء بعد.</p>}
        {customers && customers.length > 0 && (
          <ListFilter
            filter={filter}
            onChange={setFilter}
            placeholder="دوّر بالاسم أو الكود أو التليفون أو التسجيل الضريبى"
            shown={shownCustomers.length}
            total={customers.length}
            statuses={STATUSES}
            emptyStatusLabel="النشطين"
          />
        )}
        {customers && customers.length > 0 && shownCustomers.length === 0 && (
          <p className="sub">مفيش نتايج للبحث ده.</p>
        )}
        {customers && shownCustomers.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>الكود</th>
                  <th>الاسم</th>
                  <th>النوع</th>
                  <th>التسجيل الضريبى</th>
                  <th>الهاتف</th>
                  <th>مدة السداد</th>
                  {balances && <th>الرصيد</th>}
                  <th>الحالة</th>
                  {canCreate && <th />}
                </tr>
              </thead>
              <tbody>
                {shownCustomers.map((customer) => (
                  <tr
                    key={customer.id}
                    className="clickable"
                    tabIndex={0}
                    aria-selected={statementOf === customer.id}
                    onClick={() => openStatement(customer.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') openStatement(customer.id);
                    }}
                  >
                    <td className="mono">{customer.code}</td>
                    <td>{customer.nameAr}</td>
                    <td>{PERSON_LABELS[customer.personType]}</td>
                    <td className="mono">{customer.taxRegistrationNumber ?? ''}</td>
                    <td className="mono">{customer.phone ?? ''}</td>
                    <td>{customer.paymentTermsDays} يوم</td>
                    {balances && (
                      <td className="num-cell">{formatMoney(balances.get(customer.id) ?? '0')}</td>
                    )}
                    <td>{customer.isActive ? 'نشط' : <span className="pill">موقوف</span>}</td>
                    {canCreate && (
                      <td>
                        <button
                          type="button"
                          className="link"
                          onClick={(event) => {
                            event.stopPropagation();
                            startEdit(customer);
                          }}
                        >
                          تعديل
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {balances && (
                <tfoot>
                  <tr className="closing">
                    <td colSpan={6}>الإجمالى</td>
                    <td className="num-cell total">
                      {formatMoney(
                        sumAmounts(shownCustomers.map((item) => balances.get(item.id) ?? '0')),
                      )}
                    </td>
                    <td colSpan={canCreate ? 2 : 1} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
        {customers && customers.length > 0 && <p className="label">اضغط أى عميل لفتح كشف حسابه.</p>}
      </div>

      {statementOf && (
        <div id="party-statement">
          <PartyStatement
            key={statementOf}
            companyId={companyId}
            kind="customer"
            partyId={statementOf}
            currency={currency}
            onSessionLost={onSessionLost}
          />
        </div>
      )}
    </>
  );
}
